<template>
  <section v-if="loading || hasData">
    <page-sub-header
      :title="bankingInfo.save.label"
      :subtitle="isDataReady ? timeSeriesSubtitle : undefined"
      class="mt-4"
    >
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady"
            :file-name="`build-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else>
      <chart-with-companion-kpis
        v-if="timeGroupedPotsOpenedDataset.values.length"
        card-class="mb-4"
        chart-title="Number of savers over time"
        chart-subtitle="Number of employees"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedPotsOpenedDataset"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedPotsOpenedDataset.name } } },
            ...createCountWithPercentageTooltip(timeGroupedPotsOpenedPercentageDataset),
          }"
          fill-gradient
        />
        <template #companion>
          <div class="stats-combined mb-3">
            <app-stat
              :stat="lastGroupedPotsOpened"
              :previous-stat="prevGroupedPotsOpened"
              :formatter="toInteger"
              description="Savers"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
            <app-stat
              :stat="lastGroupedPotsOpenedPercentage"
              :previous-stat="prevGroupedPotsOpenedPercentage"
              :formatter="toNumber"
              suffix="%"
              description="Savers Percentage"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
          </div>
          <p class="mb-0">
            {{ timeGroupTitle }}, {{ toInteger(lastGroupedPotsOpened) }} employees ({{
              lastGroupedPotsOpenedPercentage
            }}%) had a {{ bankingInfo.save.potLabel }}.
            {{ potsOpenedPrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>

      <!-- Row 1: Savings metrics -->
      <b-row>
        <b-col cols="12" :lg="tescoPortalStubsEnabled ? 4 : 6" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="totalIngoingsDataset.calculated.mean || 0"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbolFromCode(State.state.company.default_currency)"
              :description="
                tescoPortalStubsEnabled
                  ? `Average ${timeSeriesUnit}ly deposits total (${currencySymbolFromCode(
                      State.state.company.default_currency,
                    )}${toInteger(totalContributionsSinceInception)} total contributions since inception)`
                  : `Average ${timeSeriesUnit}ly deposits total`
              "
              :class="[totalIngoingsDataset.values.length && 'mb-3']"
              background-color="core-5-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart v-if="totalIngoingsDataset.values.length" title="Deposits total">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="totalIngoingsDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.CURRENCY, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col cols="12" :lg="tescoPortalStubsEnabled ? 4 : 6" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="averageEOMDataset.calculated.mean || 0"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbolFromCode(State.state.company.default_currency)"
              description="Average end of month balance"
              :class="[timeGroupedAverageEOMDataset.values.length && 'mb-3']"
              background-color="core-4-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart v-if="timeGroupedAverageEOMDataset.values.length" title="Average end of month balance">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="timeGroupedAverageEOMDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.CURRENCY, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <!-- Tesco Demo: Current total savings balance -->
        <b-col v-if="tescoPortalStubsEnabled" cols="12" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="currentSavingsBalance"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbolFromCode(State.state.company.default_currency)"
              description="Current total savings balance"
              :class="[currentSavingsBalanceDataset.values.length && 'mb-3']"
              background-color="core-3-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart v-if="currentSavingsBalanceDataset.values.length" title="Current total savings balance">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="currentSavingsBalanceDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.CURRENCY, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
      </b-row>

      <!-- Row 2: Prize metrics -->
      <b-row v-if="hasPrizeMetrics">
        <b-col cols="12" lg="6" class="mb-4 mb-lg-0">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="prizeWinnersDataset.calculated.mean || 0"
              :formatter="Math.round"
              :description="`Average ${timeSeriesUnit}ly prize winners`"
              :class="[prizeWinnersDataset.values.length && 'mb-3']"
              background-color="core-3-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart v-if="prizeWinnersDataset.values.length" title="Prize winners">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="prizeWinnersDataset"
                :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col cols="12" lg="6" class="mb-4 mb-lg-0">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="(totalPrizeWinningsDataset.calculated.mean || 0) / 100"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbolFromCode(State.state.company.default_currency)"
              :description="`Average ${timeSeriesUnit}ly prize amount won`"
              :class="[totalPrizeWinningsDataset.values.length && 'mb-3']"
              background-color="core-1-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart v-if="totalPrizeWinningsDataset.values.length" title="Prize amount won">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="totalPrizeWinningsDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.CURRENCY, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
      </b-row>

      <!-- New merged savings metrics (feature-flagged) -->
      <template v-if="portalUpdatesJan2026Enabled">
        <!-- Cumulative Deposits Combo Chart -->
        <chart-with-companion-kpis
          v-if="timeGroupedMonthlyPoundsDataset.values.length"
          card-class="mt-4 mb-4"
          chart-title="Monthly and cumulative deposits"
        >
          <combo-chart
            :labels="timeGroupDataset"
            :bar-dataset="timeGroupedMonthlyPoundsDataset"
            :line-dataset="timeGroupedCumulativePoundsDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
            }"
            fill-gradient
          />
          <template #companion>
            <app-stat
              :stat="lastMonthlyPounds"
              :previous-stat="prevMonthlyPounds"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbol"
              description="Last period deposits"
              class="mb-3"
              background-color="core-5-lighten-90"
              color="default"
              pdf-kpi
            />
            <app-stat
              :stat="lastCumulativePounds"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbol"
              description="Cumulative deposits"
              background-color="core-3-lighten-80"
              color="default"
              pdf-kpi
            />
          </template>
        </chart-with-companion-kpis>

        <!-- Interest Paid Section -->
        <chart-with-companion-kpis
          v-if="timeGroupedInterestDataset.values.length"
          card-class="mb-4"
          chart-title="Interest paid over time"
        >
          <line-chart
            :labels="timeGroupDataset"
            :datasets="timeGroupedInterestDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: { y: { title: { text: 'Interest paid' } } },
            }"
            fill-gradient
          />
          <template #companion>
            <app-stat
              :stat="lastInterestPounds"
              :previous-stat="prevInterestPounds"
              :formatter="toNumber"
              :prefix="currencySymbol"
              description="Interest paid last period"
              class="mb-3"
              background-color="core-2-lighten-90"
              color="default"
              pdf-kpi
            />
            <app-stat
              :stat="lastCumulativeInterest"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbol"
              description="Cumulative interest paid"
              background-color="core-2-lighten-80"
              color="default"
              pdf-kpi
            />
          </template>
        </chart-with-companion-kpis>

        <!-- External Deposits Row (only shown if data exists) -->
        <b-row v-if="hasExternalDeposits">
          <b-col cols="12" lg="6" class="mb-4">
            <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
              <app-stat
                :stat="totalExternalDepositors"
                :formatter="toInteger"
                description="External depositors"
                :class="[timeGroupedExternalDepositorsDataset.values.length && 'mb-3']"
                background-color="core-3-lighten-90"
                color="default"
                small
                pdf-kpi
              />
              <p class="text-muted mb-0">Users making deposits outside of payroll</p>
              <pdf-chart v-if="timeGroupedExternalDepositorsDataset.values.length" title="External depositors">
                <line-chart
                  :labels="timeGroupDataset"
                  :datasets="timeGroupedExternalDepositorsDataset"
                  :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
                  :display-points="false"
                  class="mt-auto"
                  fill-gradient
                />
              </pdf-chart>
            </sme-card>
          </b-col>
          <b-col cols="12" lg="6" class="mb-4">
            <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
              <app-stat
                :stat="totalExternalDepositPounds"
                :formatter="value => toInteger(Math.round(value))"
                :prefix="currencySymbol"
                description="External deposit value"
                :class="[timeGroupedExternalDepositPoundsDataset.values.length && 'mb-3']"
                background-color="core-3-lighten-80"
                color="default"
                small
                pdf-kpi
              />
              <pdf-chart v-if="timeGroupedExternalDepositPoundsDataset.values.length" title="External deposit value">
                <line-chart
                  :labels="timeGroupDataset"
                  :datasets="timeGroupedExternalDepositPoundsDataset"
                  :chart-options="merge({}, CHART_PRESET_OPTIONS.CURRENCY, CHART_PRESET_OPTIONS.MINIMAL)"
                  :display-points="false"
                  class="mt-auto"
                  fill-gradient
                />
              </pdf-chart>
            </sme-card>
          </b-col>
        </b-row>
      </template>
    </template>
  </section>
</template>

<script setup>
import moment from 'moment';
import merge from 'lodash/merge';
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import PdfChart from '@/components/PdfChart.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import ComboChart from '@/components/charts/ComboChart.vue';
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import State from '@/state/State';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { createCountWithPercentageTooltip, getInitialAspectRatio, TIME_SERIES_VALUES } from '@/utils/chart';
import { toInteger, toNumber, currencySymbolFromCode } from '@/utils/common';
import ExportButton from '@/components/ExportButton.vue';

const { tescoPortalStubsEnabled, portalUpdatesJan2026Enabled } = useFeatureFlags();
const { divisionId } = useDivisionSelection();

const loading = ref(true);
const timeDataset = ref(null);
const potsOpenedDataset = ref(null);
const potsOpenedPercentageDataset = ref(null);
const averageEOMDataset = ref(null);
const totalIngoingsDataset = ref(null);
const prizeWinnersDataset = ref(null);
const totalPrizeWinningsDataset = ref(null);

// New datasets for merged savings metrics (feature-flagged)
const depositCountDataset = ref({ values: [], name: 'Deposit count' });
const monthlyPoundsDataset = ref({ values: [], name: 'Monthly deposits' });
const cumulativePoundsDataset = ref({ values: [], name: 'Cumulative deposits' });
const interestPoundsDataset = ref({ values: [], name: 'Interest paid' });
const cumulativeInterestDataset = ref({ values: [], name: 'Cumulative interest' });
const externalDepositorsDataset = ref({ values: [], name: 'External depositors' });
const externalDepositPoundsDataset = ref({ values: [], name: 'External deposit value' });

const {
  timeGroupDataset,
  timeGroupTitle,
  timeSeries,
  timeSeriesUnit,
  timeSeriesSubtitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);

const timeGroupedPotsOpenedDataset = computed(() => datasetToTimeGroupedDataset(potsOpenedDataset));
const timeGroupedPotsOpenedPercentageDataset = computed(() => datasetToTimeGroupedDataset(potsOpenedPercentageDataset));
const timeGroupedAverageEOMDataset = computed(() => datasetToTimeGroupedDataset(averageEOMDataset));
const lastGroupedPotsOpened = computed(() => timeGroupedPotsOpenedDataset.value?.calculated.last);
const prevGroupedPotsOpened = computed(() => timeGroupedPotsOpenedDataset.value?.calculated.prev);
const lastGroupedPotsOpenedPercentage = computed(() => timeGroupedPotsOpenedPercentageDataset.value?.calculated.last);
const prevGroupedPotsOpenedPercentage = computed(() => timeGroupedPotsOpenedPercentageDataset.value?.calculated.prev);
const potsOpenedPrevDiffText = computed(() => getPrevDiffText(timeGroupedPotsOpenedDataset));
const hasPrizeMetrics = computed(() => prizeWinnersDataset.value.values.some(value => value));
const bankingInfo = computed(() => getBankingInfo());
const currencySymbol = computed(() => currencySymbolFromCode(State.state.company.default_currency));

// New computed properties for merged savings metrics
const timeGroupedMonthlyPoundsDataset = computed(() => datasetToTimeGroupedDataset(monthlyPoundsDataset));
const timeGroupedCumulativePoundsDataset = computed(() => datasetToTimeGroupedDataset(cumulativePoundsDataset));
const timeGroupedInterestDataset = computed(() => datasetToTimeGroupedDataset(interestPoundsDataset));
const timeGroupedCumulativeInterestDataset = computed(() => datasetToTimeGroupedDataset(cumulativeInterestDataset));
const timeGroupedExternalDepositorsDataset = computed(() => datasetToTimeGroupedDataset(externalDepositorsDataset));
const timeGroupedExternalDepositPoundsDataset = computed(() =>
  datasetToTimeGroupedDataset(externalDepositPoundsDataset),
);

const lastMonthlyPounds = computed(() => timeGroupedMonthlyPoundsDataset.value?.calculated.last || 0);
const prevMonthlyPounds = computed(() => timeGroupedMonthlyPoundsDataset.value?.calculated.prev);
const lastCumulativePounds = computed(() => timeGroupedCumulativePoundsDataset.value?.calculated.last || 0);

const lastInterestPounds = computed(() => timeGroupedInterestDataset.value?.calculated.last || 0);
const prevInterestPounds = computed(() => timeGroupedInterestDataset.value?.calculated.prev);
const lastCumulativeInterest = computed(() => timeGroupedCumulativeInterestDataset.value?.calculated.last || 0);

const hasExternalDeposits = computed(() => {
  const values = timeGroupedExternalDepositorsDataset.value?.values || [];
  return values.some(val => val > 0);
});

const totalExternalDepositors = computed(() => {
  const values = timeGroupedExternalDepositorsDataset.value?.values || [];
  return Math.max(...values, 0);
});

const totalExternalDepositPounds = computed(() => {
  const values = timeGroupedExternalDepositPoundsDataset.value?.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});

// Tesco Demo: calculate total contributions estimate once
const cachedTotalContributions = ref(0);
const hasCalculatedContributions = ref(false);
watch(
  () => ({
    launchDate: State.state.company?.properties?.launch_date,
    periodAverage: totalIngoingsDataset.value?.calculated?.mean || 0,
    timeSeries: timeSeries.value,
  }),
  ({ launchDate, periodAverage, timeSeries: currentTimeSeries }) => {
    if (launchDate && periodAverage && !hasCalculatedContributions.value) {
      const periodsSinceLaunch = Math.max(
        1,
        moment().diff(moment(launchDate), currentTimeSeries === 'w2w' ? 'weeks' : 'months'),
      );
      cachedTotalContributions.value = Math.round(periodAverage * periodsSinceLaunch * 0.65);
      hasCalculatedContributions.value = true;
    }
  },
  { immediate: true },
);

const totalContributionsSinceInception = computed(() => cachedTotalContributions.value);

// Tesco Demo: Current savings balance dataset (multiply savers by avg balance)
const currentSavingsBalanceDataset = computed(() => {
  if (!tescoPortalStubsEnabled.value) {
    return { values: [], name: 'Current Balance (£)' };
  }

  // Multiply the Number of savers series by Average end of month balance series
  const saversValues = timeGroupedPotsOpenedDataset.value?.values || [];
  const avgBalanceValues = timeGroupedAverageEOMDataset.value?.values || [];

  if (!saversValues.length || !avgBalanceValues.length) {
    return { values: [], name: 'Current Balance (£)' };
  }

  const balanceValues = saversValues.map((savers, index) => {
    const avgBalance = avgBalanceValues[index] || 0;
    return Math.round(savers * avgBalance);
  });

  return {
    values: balanceValues,
    name: 'Current Balance (£)',
    lineColor: PaletteColors['core-3'],
  };
});

const currentSavingsBalance = computed(() => {
  // Just use the last value from the dataset (same calculation)
  const dataset = currentSavingsBalanceDataset.value;
  return dataset.values[dataset.values.length - 1] || 0;
});

const isDataReady = computed(() => {
  return (
    timeGroupDataset.value?.values &&
    potsOpenedDataset.value?.values &&
    potsOpenedPercentageDataset.value?.values &&
    averageEOMDataset.value?.values &&
    totalIngoingsDataset.value?.values &&
    prizeWinnersDataset.value?.values &&
    totalPrizeWinningsDataset.value?.values &&
    !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => {
    const baseData = {
      time,
      builders: potsOpenedDataset.value.values[index],
      percentage: potsOpenedPercentageDataset.value.values[index],
      averageBalance: averageEOMDataset.value.values[index],
      totalDeposits: totalIngoingsDataset.value.values[index],
      prizeWinners: prizeWinnersDataset.value.values[index],
      prizeAmount: totalPrizeWinningsDataset.value.values[index],
    };

    // Add new savings metrics when feature flag is enabled
    if (portalUpdatesJan2026Enabled.value) {
      return {
        ...baseData,
        monthlyPounds: monthlyPoundsDataset.value.values[index],
        cumulativePounds: cumulativePoundsDataset.value.values[index],
        interestPounds: interestPoundsDataset.value.values[index],
        cumulativeInterest: cumulativeInterestDataset.value.values[index],
        externalDepositors: externalDepositorsDataset.value.values[index],
        externalDepositPounds: externalDepositPoundsDataset.value.values[index],
      };
    }

    return baseData;
  });
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  const baseHeaders = [
    'Date',
    potsOpenedDataset.value.name,
    potsOpenedPercentageDataset.value.name,
    'Average Balance',
    totalIngoingsDataset.value.name,
    prizeWinnersDataset.value.name,
    totalPrizeWinningsDataset.value.name,
  ];

  // Add new headers when feature flag is enabled
  if (portalUpdatesJan2026Enabled.value) {
    return [
      ...baseHeaders,
      'Monthly Deposits',
      'Cumulative Deposits',
      'Interest Paid',
      'Cumulative Interest',
      'External Depositors',
      'External Deposit Value',
    ];
  }

  return baseHeaders;
});

const exportFileRows = item => {
  const baseRows = [
    item.time,
    item.builders,
    item.percentage,
    Math.round(item.averageBalance),
    Math.round(item.totalDeposits),
    item.prizeWinners,
    Math.round(item.prizeAmount),
  ];

  // Add new values when feature flag is enabled
  if (portalUpdatesJan2026Enabled.value) {
    return [
      ...baseRows,
      Math.round(item.monthlyPounds || 0),
      Math.round(item.cumulativePounds || 0),
      Math.round((item.interestPounds || 0) * 100) / 100,
      Math.round(item.cumulativeInterest || 0),
      item.externalDepositors || 0,
      Math.round(item.externalDepositPounds || 0),
    ];
  }

  return baseRows;
};

watch([divisionId, timeSeries], () => getBuildMetrics());

const getBuildMetrics = async () => {
  loading.value = true;

  // Build the list of API calls to make
  const apiCalls = [ApiClient.getBuildMetrics(timeSeries.value, divisionId.value)];

  // Add new savings metrics API calls when feature flag is enabled
  if (portalUpdatesJan2026Enabled.value) {
    apiCalls.push(
      ApiClient.getPortalDeposits(timeSeries.value, divisionId.value),
      ApiClient.getPortalInterest(timeSeries.value, divisionId.value),
      ApiClient.getPortalExternalDeposits(timeSeries.value, divisionId.value),
    );
  }

  const responses = await Promise.all(apiCalls);
  const { results } = responses[0];

  if (results?.length) {
    const time = results.find(result => result.id === 'time');
    const potsOpened = results.find(result => result.id === 'pots_opened');
    const enrolled = results.find(result => result.id === 'enrolled');
    const averageEOMBalance = results.find(result => result.id === 'avg_eom_pot_balance');
    const totalIngoings = results.find(result => result.id === 'total_ingoings');
    const prizeWinners = results.find(result => result.id === 'number_of_prize_winners');
    const totalPrizeWinnings = results.find(result => result.id === 'total_prize_amount_won_pence');

    timeDataset.value = time;
    potsOpenedDataset.value = {
      ...potsOpened,
      name: 'Number of savers',
    };
    potsOpenedPercentageDataset.value = {
      name: 'Percentage (%)',
      values: enrolled.values.map((value, index) =>
        value ? Number(toNumber((potsOpened.values[index] / value) * 100)) : 0,
      ),
    };
    averageEOMDataset.value = {
      ...averageEOMBalance,
      name: 'Balance',
      lineColor: PaletteColors['core-4'],
    };
    totalIngoingsDataset.value = {
      ...totalIngoings,
      name: 'Deposits total',
      lineColor: PaletteColors['core-5'],
    };
    prizeWinnersDataset.value = {
      ...prizeWinners,
      name: 'Prize winners',
      lineColor: PaletteColors['core-3'],
    };
    totalPrizeWinningsDataset.value = {
      ...totalPrizeWinnings,
      name: 'Prize amount won',
      values: totalPrizeWinnings.values.map(value => toNumber(value / 100)),
      lineColor: PaletteColors['core-2-lighten-20'],
    };
  }

  // Process new savings metrics when feature flag is enabled
  if (portalUpdatesJan2026Enabled.value && responses.length > 1) {
    const depositsResponse = responses[1];
    const interestResponse = responses[2];
    const externalResponse = responses[3];

    if (depositsResponse?.results?.length) {
      const depositCount = depositsResponse.results.find(result => result.id === 'deposit_count');
      const monthlyPounds = depositsResponse.results.find(result => result.id === 'monthly_pounds');
      const cumulativePounds = depositsResponse.results.find(result => result.id === 'cumulative_pounds');

      if (depositCount) {
        depositCountDataset.value = {
          ...depositCount,
          name: 'Deposit count',
          lineColor: PaletteColors['core-1'],
        };
      }
      if (monthlyPounds) {
        monthlyPoundsDataset.value = {
          ...monthlyPounds,
          name: 'Monthly deposits',
          barColor: PaletteColors['core-5'],
        };
      }
      if (cumulativePounds) {
        cumulativePoundsDataset.value = {
          ...cumulativePounds,
          name: 'Cumulative deposits',
          lineColor: PaletteColors['core-3'],
        };
      }
    }

    if (interestResponse?.results?.length) {
      const interestPounds = interestResponse.results.find(result => result.id === 'interest_pounds');
      const cumulativeInterest = interestResponse.results.find(result => result.id === 'cumulative_interest_pounds');
      if (interestPounds) {
        interestPoundsDataset.value = {
          ...interestPounds,
          name: 'Interest paid',
          lineColor: PaletteColors['core-2'],
        };
      }
      if (cumulativeInterest) {
        cumulativeInterestDataset.value = {
          ...cumulativeInterest,
          name: 'Cumulative interest',
          lineColor: PaletteColors['core-2'],
        };
      }
    }

    if (externalResponse?.results?.length) {
      const externalTime = externalResponse.results.find(result => result.id === 'time');
      const externalDepositors = externalResponse.results.find(result => result.id === 'external_depositors');
      const externalPounds = externalResponse.results.find(result => result.id === 'external_deposit_pounds');

      // Align external data to main time array
      const mainTimeValues = timeDataset.value?.values || [];
      const alignValues = (externalTimeArr, valuesArr) => {
        if (!externalTimeArr?.length || !valuesArr?.length) {
          return [];
        }
        const valueMap = {};
        externalTimeArr.forEach((t, i) => {
          valueMap[t] = valuesArr[i];
        });
        return mainTimeValues.map(t => valueMap[t] ?? 0);
      };

      if (externalDepositors && externalTime) {
        externalDepositorsDataset.value = {
          ...externalDepositors,
          values: alignValues(externalTime.values, externalDepositors.values),
          name: 'External depositors',
          lineColor: PaletteColors['core-3'],
        };
      }
      if (externalPounds && externalTime) {
        externalDepositPoundsDataset.value = {
          ...externalPounds,
          values: alignValues(externalTime.values, externalPounds.values),
          name: 'External deposit value',
          lineColor: PaletteColors['core-3'],
        };
      }
    }
  }

  loading.value = false;
};

onBeforeMount(() => getBuildMetrics());

const hasData = computed(() => !loading.value && !!timeGroupedPotsOpenedDataset.value?.values?.length);

defineExpose({ hasData });
</script>

<style lang="scss" scoped>
.stats-combined {
  display: flex;

  :deep(.app-stat) {
    flex: 1;
    height: auto; // Override height: 100% from AppStat to allow flexbox stretch
    border-radius: 0;
    box-shadow: none;

    &:first-child {
      border-radius: 4px 0 0 4px;
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
    }

    .app-stat__value-container .app-stat__value {
      font-size: 28px;

      @media (min-width: 992px) {
        font-size: 32px;
      }
    }

    .app-stat__value-container {
      height: auto;
      min-height: 2rem;
    }
  }
}
</style>
